import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  getForecastRange,
  type ForecastHorizon,
  type ForecastPeriod
} from './forecast-cash-flow.service';

export type { ForecastHorizon, ForecastPeriod };

export type ChurnRiskLevel = 'HEALTHY' | 'WATCH' | 'CRITICAL';

export interface ChurnSubscriptionRow {
  id: number;
  number?: string;
  customerName: string;
  planName: string;
  status: string;
  frequency?: string;
  collectionMethod?: string;
  total: number;
  mrr: number;
  risk: ChurnRiskLevel;
  nextBillingDate?: string;
  endDate?: string;
  updatedAt?: string;
  reason: string;
}

export interface ChurnMonthBucket {
  key: string;
  label: string;
  /** ACTIVE + TRAILING (renovaciones proyectadas en el mes) */
  activeCount: number;
  activeMrr: number;
  churnedCount: number;
  churnedMrr: number;
  atRiskCount: number;
  atRiskMrr: number;
}

export interface ChurnKpis {
  healthyMrr: number;
  healthyCount: number;
  atRiskMrr: number;
  atRiskCount: number;
  churnedMrr: number;
  churnedCount: number;
  logoRetentionRate: number;
  revenueRetentionRate: number;
  atRiskRate: number;
  upcomingAtRiskCount: number;
  upcomingAtRiskMrr: number;
}

export interface ChurnForecastData {
  from: string;
  to: string;
  kpis: ChurnKpis;
  byStatus: { status: string; count: number; mrr: number }[];
  byFrequency: { frequency: string; count: number; mrr: number }[];
  months: ChurnMonthBucket[];
  atRisk: ChurnSubscriptionRow[];
  churned: ChurnSubscriptionRow[];
  upcomingRenewals: ChurnSubscriptionRow[];
  loading: boolean;
  error?: string;
}

interface ChurnKpisApi {
  healthyMrr?: number;
  healthyCount?: number;
  atRiskMrr?: number;
  atRiskCount?: number;
  churnedMrr?: number;
  churnedCount?: number;
  logoRetentionRate?: number;
  revenueRetentionRate?: number;
  atRiskRate?: number;
  upcomingAtRiskCount?: number;
  upcomingAtRiskMrr?: number;
}

interface ChurnBreakdownApi {
  key?: string;
  count?: number;
  mrr?: number;
}

interface ChurnMonthApi {
  month?: string;
  activeCount?: number;
  activeMrr?: number;
  churnedCount?: number;
  churnedMrr?: number;
  atRiskCount?: number;
  atRiskMrr?: number;
}

interface ChurnRowApi {
  id?: number;
  number?: string;
  customerName?: string;
  planName?: string;
  status?: string;
  frequency?: string;
  collectionMethod?: string;
  total?: number;
  mrr?: number;
  risk?: ChurnRiskLevel;
  nextBillingDate?: string;
  endDate?: string;
  updatedAt?: string;
  reason?: string;
}

interface ChurnForecastApi {
  from?: string;
  to?: string;
  kpis?: ChurnKpisApi;
  byStatus?: ChurnBreakdownApi[];
  byFrequency?: ChurnBreakdownApi[];
  months?: ChurnMonthApi[];
  atRisk?: ChurnRowApi[];
  churned?: ChurnRowApi[];
  upcomingRenewals?: ChurnRowApi[];
}

interface WrapperChurnForecast {
  ok?: boolean;
  message?: string;
  body?: ChurnForecastApi | null;
}

function toDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseLocalDate(iso: string | undefined): Date | null {
  if (!iso || iso.length < 10) return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function formatMonthLabel(iso: string | undefined): string {
  const date = parseLocalDate(iso);
  if (!date) return iso || '';
  return date.toLocaleDateString('es-MX', { month: 'short', year: 'numeric' });
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function mapRow(r: ChurnRowApi): ChurnSubscriptionRow {
  return {
    id: r.id ?? 0,
    number: r.number,
    customerName: r.customerName || '—',
    planName: r.planName || 'Sin plan',
    status: r.status || '—',
    frequency: r.frequency,
    collectionMethod: r.collectionMethod,
    total: num(r.total),
    mrr: num(r.mrr),
    risk: r.risk === 'WATCH' || r.risk === 'CRITICAL' ? r.risk : 'HEALTHY',
    nextBillingDate: r.nextBillingDate?.slice(0, 10),
    endDate: r.endDate?.slice(0, 10),
    updatedAt: r.updatedAt,
    reason: r.reason || ''
  };
}

@Injectable({ providedIn: 'root' })
export class ForecastChurnService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  load(period: ForecastPeriod = { horizon: '6m' }): Observable<ChurnForecastData> {
    const { from, to } = getForecastRange(period);
    const params = new HttpParams().set('from', toDayKey(from)).set('to', toDayKey(to));

    return this.http.get<WrapperChurnForecast>(`${this.apiUrl}/forecasts/churn`, { params }).pipe(
      map((res) => {
        if (!res?.ok || !res.body) {
          return this.empty(from, to, res?.message || 'No se pudo cargar el forecast de churn');
        }
        return this.mapApi(res.body);
      }),
      catchError((err) =>
        of(this.empty(from, to, err?.error?.message || err?.message || 'Error al cargar el forecast de churn'))
      )
    );
  }

  private mapApi(body: ChurnForecastApi): ChurnForecastData {
    const k = body.kpis ?? {};
    return {
      from: body.from ?? '',
      to: body.to ?? '',
      kpis: {
        healthyMrr: num(k.healthyMrr),
        healthyCount: num(k.healthyCount),
        atRiskMrr: num(k.atRiskMrr),
        atRiskCount: num(k.atRiskCount),
        churnedMrr: num(k.churnedMrr),
        churnedCount: num(k.churnedCount),
        logoRetentionRate: num(k.logoRetentionRate),
        revenueRetentionRate: num(k.revenueRetentionRate),
        atRiskRate: num(k.atRiskRate),
        upcomingAtRiskCount: num(k.upcomingAtRiskCount),
        upcomingAtRiskMrr: num(k.upcomingAtRiskMrr)
      },
      byStatus: (body.byStatus ?? []).map((r) => ({
        status: r.key ?? '—',
        count: num(r.count),
        mrr: num(r.mrr)
      })),
      byFrequency: (body.byFrequency ?? []).map((r) => ({
        frequency: r.key ?? '—',
        count: num(r.count),
        mrr: num(r.mrr)
      })),
      months: (body.months ?? []).map((m) => ({
        key: (m.month ?? '').slice(0, 7),
        label: formatMonthLabel(m.month),
        activeCount: num(m.activeCount),
        activeMrr: num(m.activeMrr),
        churnedCount: num(m.churnedCount),
        churnedMrr: num(m.churnedMrr),
        atRiskCount: num(m.atRiskCount),
        atRiskMrr: num(m.atRiskMrr)
      })),
      atRisk: (body.atRisk ?? []).map(mapRow),
      churned: (body.churned ?? []).map(mapRow),
      upcomingRenewals: (body.upcomingRenewals ?? []).map(mapRow),
      loading: false
    };
  }

  private empty(from: Date, to: Date, error?: string): ChurnForecastData {
    return {
      from: toDayKey(from),
      to: toDayKey(to),
      kpis: {
        healthyMrr: 0,
        healthyCount: 0,
        atRiskMrr: 0,
        atRiskCount: 0,
        churnedMrr: 0,
        churnedCount: 0,
        logoRetentionRate: 100,
        revenueRetentionRate: 100,
        atRiskRate: 0,
        upcomingAtRiskCount: 0,
        upcomingAtRiskMrr: 0
      },
      byStatus: [],
      byFrequency: [],
      months: [],
      atRisk: [],
      churned: [],
      upcomingRenewals: [],
      loading: false,
      error
    };
  }
}
